import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

interface PhotoViewerProps {
  photos: string[];
  isOpen: boolean;
  onClose: () => void;
  initialIndex?: number;
}

export default function PhotoViewer({ photos, isOpen, onClose, initialIndex = 0 }: PhotoViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialIndex]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, photos.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  if (!isOpen || photos.length === 0) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black bg-opacity-50 flex items-center justify-center p-2">
      {/* Main Image Container */}
      <div className="relative w-full max-w-md h-full max-h-[98vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Photo Counter */}
        {photos.length > 1 && (
          <div className="absolute top-3 right-3 z-10 bg-white bg-opacity-90 text-gray-800 px-2 py-1 rounded-full text-xs font-medium shadow-lg">
            {currentIndex + 1} / {photos.length}
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 left-3 z-10 bg-white/80 rounded-full p-2 shadow hover:bg-blue-50 transition-colors"
          aria-label="Close"
        >
          <ArrowLeft className="h-5 w-5 text-blue-700" />
        </button>

        {/* Navigation Buttons */}
        {photos.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 text-gray-800 rounded-full p-2 hover:bg-opacity-100 transition-all shadow-lg"
              aria-label="Previous photo"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 z-10 bg-white bg-opacity-90 text-gray-800 rounded-full p-2 hover:bg-opacity-100 transition-all shadow-lg"
              aria-label="Next photo"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Image Display Area */}
        <div className="flex items-center justify-center w-full h-full">
          <img
            src={photos[currentIndex]}
            alt={`Property photo ${currentIndex + 1}`}
            className="w-full h-full object-fill transition-transform duration-200"
            draggable={false}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = '/placeholder.jpg';
            }}
          />
        </div>






        {/* Thumbnail Strip (for multiple photos) */}
        {photos.length > 1 && (
          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-10 flex gap-1 max-w-full overflow-x-auto px-2">
            {photos.map((photo, index) => (
              <button
                key={index}
                onClick={() => {
                  setCurrentIndex(index);
                }}
                className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-all shadow-lg ${
                  index === currentIndex
                    ? 'border-blue-500'
                    : 'border-gray-300 hover:border-gray-500'
                }`}
              >
                <img
                  src={photo}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                  draggable={false}
                />
              </button>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
